import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAgencyContext, useAuth, useToast } from '../contexts/AppContexts';
import { AlertTriangle, BookOpen, Building2, CalendarDays, ChevronDown, ChevronRight, Edit, MapPin, Plus, Printer, Search, Target, Trash2, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { API_BASE_URL } from '../config';
import ExerciseDetailView from './ExerciseDetailView';
import { writeSanitizedHTML } from '../utils/safePrint';
import secureStorage from '../utils/secureStorage';
import { useHiraThumbnailCache, useExerciseImageCache } from '../utils/hiraThumbnails';

const API = `${API_BASE_URL}/api`;

const ExerciseListView = () => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [agenciesMap, setAgenciesMap] = useState({}); // Map of agency ID to agency name
  const [expandedExercises, setExpandedExercises] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const toggleExpanded = (id, e) => {
    if (e) e.stopPropagation();
    setExpandedExercises(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toast = useToast();
  const { token } = useAuth(); // Get token from auth context
  const { selectedAgency } = useAgencyContext();
  // Lazy-loaded HIRA thumbnails keyed by hira_id → data URI, used as an
  // avatar fallback when an exercise doesn't have its own image.
  const hiraThumbCache = useHiraThumbnailCache(exercises.map((e) => e.hira_id).filter(Boolean));
  // Lazy-loaded exercise images (the list endpoint strips them; this hook
  // re-hydrates each row's own uploaded image via a dedicated endpoint).
  const exImageCache = useExerciseImageCache(exercises);

  console.log('=== ExerciseListView RENDER ===');
  console.log('Exercises count:', exercises.length);

  // Fetch agencies to create a mapping
  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const authToken = token || secureStorage.getToken();
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
        const response = await axios.get(`${API}/agencies`, { headers });
        const map = {};
        response.data.forEach(agency => {
          map[agency.id] = agency.name;
        });
        setAgenciesMap(map);
      } catch (error) {
        console.error('Error fetching agencies for mapping:', error);
      }
    };
    fetchAgencies();
  }, [token]);

  useEffect(() => {
    fetchExercises();
  }, [selectedAgency]); // Re-fetch when agency context changes

  const fetchExercises = async () => {
    try {
      // Fall back to the token stored by secureStorage if the auth context
      // hasn't hydrated yet — prevents a race where the initial mount fires
      // the API call with no Authorization header and the server returns 401.
      const authToken = token || secureStorage.getToken();
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

      // Add organization_id filter if Super Admin has selected an agency
      let url = `${API}/exercise-builder`;
      if (selectedAgency) {
        url += `?organization_id=${selectedAgency.id}`;
      }

      const response = await axios.get(url, { headers });
      setExercises(response.data);
      toast.success(`Loaded ${response.data.length} exercise(s)`);
    } catch (error) {
      console.error('Error fetching exercises:', error);
      console.error('Error details:', error.response?.data);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 401 || status === 403) {
        // Auth issue is being handled by the global interceptor (debounced).
        // Don't add a redundant toast here.
        return;
      }
      const msg = typeof detail === 'string' ? detail : `Failed to load exercises${status ? ` (HTTP ${status})` : ''}`;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const deleteExercise = async (exerciseId) => {
    if (window.confirm('Are you sure you want to delete this exercise?')) {
      try {
        await axios.delete(
          `${API}/exercise-builder/${exerciseId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        setExercises(prev => prev.filter(e => e.id !== exerciseId));
        toast.success('Exercise deleted successfully');
      } catch (error) {
        console.error('Error deleting exercise:', error);
        toast.error('Error deleting exercise. Please try again.');
      }
    }
  };

  // Print function for exercise cards
  const handlePrintExerciseCard = async (exercise, e) => {
    e.stopPropagation(); // Prevent card click
    
    // Immediate alert to confirm function is called
    alert('Print button clicked! Check console for details.');
    
    try {
      console.log('=== PRINT FUNCTION START (Exercise Card) ===');
      console.log('Exercise:', exercise);
      console.log('Exercise ID:', exercise.id);
      console.log('API constant:', API);
      console.log('Token available:', !!token);
      
      // Fetch all exercise data including goals, objectives, events, etc.
      let fullExerciseData = {};
      let injections = [];
      let coordinators = [];
      
      const headers = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      
      console.log('=== FETCHING FULL EXERCISE DATA ===');
      try {
        const exerciseResponse = await axios.get(`${API}/exercise-builder/${exercise.id}`, headers);
        fullExerciseData = exerciseResponse.data || {};
        console.log('✅ Fetched full exercise data');
        console.log('Full exercise data:', fullExerciseData);
        console.log('Goals in data:', fullExerciseData.goals);
        console.log('Objectives in data:', fullExerciseData.objectives);
      } catch (error) {
        console.error('❌ Error fetching exercise data:', error);
        console.error('Error details:', error.response?.data);
        console.warn('⚠️ Exercise data not found, but will continue to fetch MSEL data');
        // Don't return here - MSEL data is in a separate collection and may still exist
        fullExerciseData = {}; // Use empty object as fallback
      }
      
      console.log('=== FETCHING MSEL INJECTIONS ===');
      console.log('Exercise ID to fetch:', exercise.id);
      console.log('Full API URL:', `${API}/msel/${exercise.id}`);
      console.log('Using token:', token ? 'Yes' : 'No');
      
      try {
        const mselResponse = await axios.get(`${API}/msel/${exercise.id}`, headers);
        injections = mselResponse.data || [];
        console.log('✅ Fetched MSEL injections:', injections.length, 'events');
        console.log('Injections data:', injections);
        
        if (injections.length === 0) {
          console.warn('⚠️ No injections found for this exercise!');
        } else {
          console.log('First injection:', injections[0]);
          console.log('First injection exercise_id:', injections[0]?.exercise_id);
          console.log('Expected exercise_id:', exercise.id);
        }
      } catch (error) {
        console.error('❌ Error fetching MSEL:', error);
        console.error('Error details:', error.response?.data);
        console.error('Status:', error.response?.status);
        injections = [];
      }
      
      console.log('=== FETCHING COORDINATORS ===');
      try {
        const coordResponse = await axios.get(`${API}/team-coordinators/${exercise.id}`, headers);
        coordinators = coordResponse.data || [];
        console.log('✅ Fetched coordinators:', coordinators.length, 'coordinators');
        console.log('Coordinators data:', coordinators);
      } catch (error) {
        console.error('❌ Error fetching coordinators:', error);
        console.error('Error details:', error.response?.data);
        coordinators = [];
      }
      
      console.log('=== DATA BEFORE PRINT GENERATION ===');
      console.log('Full exercise data available:', !!fullExerciseData);
      console.log('Goals count:', (fullExerciseData.goals || []).length);
      console.log('Objectives count:', (fullExerciseData.objectives || []).length);
      console.log('Injections count:', injections.length);
      console.log('Injections variable:', injections);
      console.log('Coordinators count:', coordinators.length);
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow pop-ups to print the exercise report');
        return;
      }
      
      console.log('Print window opened, generating content...');
      
      // Generate print content
      const printContent = generatePrintContentForCard(fullExerciseData, injections, coordinators);
      
      console.log('Content generated, writing to window...');
      writeSanitizedHTML(printWindow.document, printContent);
      console.log('Content written, triggering print...');
      
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } catch (error) {
      console.error('Error in print function:', error);
      toast.error('Error generating print report: ' + error.message);
    }
  };
  
  // Helper function to generate print content for cards (same as detail view)
  const generatePrintContentForCard = (exerciseData, injectionsData = [], coordinatorsData = []) => {
    const goals = exerciseData.goals || [];
    const objectives = exerciseData.objectives || [];
    const events = exerciseData.events || [];
    const functions = exerciseData.functions || [];
    const organizations = exerciseData.organizations || [];
    const codeWords = exerciseData.codeWords || [];
    const callsigns = exerciseData.callsigns || [];
    const frequencies = exerciseData.frequencies || [];
    const assumptions = exerciseData.assumptions || [];
    const artificialities = exerciseData.artificialities || [];
    const safetyConcerns = exerciseData.safetyConcerns || [];
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${exerciseData.exercise_name || 'Exercise'} - Complete Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
          h1 { color: #f97316; border-bottom: 3px solid #f97316; padding-bottom: 10px; }
          h2 { color: #f97316; margin-top: 30px; border-bottom: 2px solid #f97316; padding-bottom: 5px; }
          h3 { color: #444; margin-top: 20px; }
          .section { margin-bottom: 30px; page-break-inside: avoid; }
          .field { margin: 10px 0; }
          .label { font-weight: bold; color: #666; }
          .value { margin-left: 10px; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f97316; color: white; }
          .item { background: #f9f9f9; padding: 10px; margin: 10px 0; border-left: 4px solid #f97316; }
          @media print { 
            body { margin: 20px; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>${exerciseData.exercise_name || 'Exercise Report'}</h1>
        
        <div class="section">
          <h2>Step 1: Exercise Information</h2>
          <div class="field"><span class="label">Exercise Name:</span><span class="value">${exerciseData.exercise_name || 'N/A'}</span></div>
          <div class="field"><span class="label">Exercise Type:</span><span class="value">${exerciseData.exercise_type || 'N/A'}</span></div>
          <div class="field"><span class="label">Description:</span><span class="value">${exerciseData.exercise_description || 'N/A'}</span></div>
          <div class="field"><span class="label">Location:</span><span class="value">${exerciseData.location || 'N/A'}</span></div>
          <div class="field"><span class="label">Coordinates:</span><span class="value">${exerciseData.latitude || 0}, ${exerciseData.longitude || 0}</span></div>
          <div class="field"><span class="label">Start:</span><span class="value">${exerciseData.start_date ? new Date(exerciseData.start_date).toLocaleDateString() : 'N/A'} at ${exerciseData.start_time || 'N/A'}</span></div>
          <div class="field"><span class="label">End:</span><span class="value">${exerciseData.end_date ? new Date(exerciseData.end_date).toLocaleDateString() : 'N/A'} at ${exerciseData.end_time || 'N/A'}</span></div>
          <div class="field"><span class="label">Zoom Level:</span><span class="value">${exerciseData.zoom_level || 'N/A'}</span></div>
        </div>

        <div class="section">
          <h2>Step 2: Scope</h2>
          <div class="field"><span class="label">Description:</span><span class="value">${exerciseData.scope_description || 'N/A'}</span></div>
          <div class="field"><span class="label">Hazards:</span><span class="value">${exerciseData.scope_hazards || 'N/A'}</span></div>
          <div class="field"><span class="label">Geographic Area:</span><span class="value">${exerciseData.scope_geographic_area || 'N/A'}</span></div>
          <div class="field"><span class="label">Functions:</span><span class="value">${exerciseData.scope_functions || 'N/A'}</span></div>
          <div class="field"><span class="label">Organizations:</span><span class="value">${exerciseData.scope_organizations || 'N/A'}</span></div>
          <div class="field"><span class="label">Personnel:</span><span class="value">${exerciseData.scope_personnel || 'N/A'}</span></div>
        </div>

        <div class="section">
          <h2>Step 3: Purpose</h2>
          <div class="field"><span class="label">Purpose Description:</span><span class="value">${exerciseData.purpose_description || 'N/A'}</span></div>
        </div>

        <div class="section">
          <h2>Step 4: Scenario</h2>
          <div class="field"><span class="label">Scenario Name:</span><span class="value">${exerciseData.scenario_name || 'N/A'}</span></div>
          <div class="field"><span class="label">Description:</span><span class="value">${exerciseData.scenario_description || 'N/A'}</span></div>
        </div>

        <div class="section">
          <h2>Step 5: Goals</h2>
          ${goals.length > 0 ? goals.map((goal, idx) => `
            <div class="item">
              <strong>Goal ${idx + 1}:</strong> ${goal.name}<br/>
              <span class="label">Description:</span> ${goal.description || 'N/A'}<br/>
              <span class="label">Achieved:</span> ${goal.achieved || 'No'}
            </div>
          `).join('') : '<p>No goals defined</p>'}
        </div>

        <div class="section">
          <h2>Step 6: Objectives</h2>
          ${objectives.length > 0 ? objectives.map((obj, idx) => `
            <div class="item">
              <strong>Objective ${idx + 1}:</strong> ${obj.name}<br/>
              <span class="label">Description:</span> ${obj.description || 'N/A'}<br/>
              <span class="label">Achieved:</span> ${obj.achieved || 'No'}
            </div>
          `).join('') : '<p>No objectives defined</p>'}
        </div>

        <div class="section">
          <h2>Step 7: Functions</h2>
          ${functions.length > 0 ? functions.map((func, idx) => `
            <div class="item">
              <strong>Function ${idx + 1}:</strong> ${func.name}<br/>
              <span class="label">Description:</span> ${func.description || 'N/A'}
            </div>
          `).join('') : '<p>No functions defined</p>'}
        </div>

        <div class="section">
          <h2>Step 8: Injections (MSEL)</h2>
          ${injectionsData.length > 0 ? injectionsData.map((inj, idx) => `
            <div class="item">
              <strong>Injection ${idx + 1}:</strong><br/>
              <span class="label">Event Number:</span> ${inj.event_number || 'N/A'}<br/>
              <span class="label">Scenario Time:</span> ${inj.scenario_time || 'N/A'}<br/>
              <span class="label">Event Type:</span> ${inj.event_type || 'N/A'}<br/>
              <span class="label">Inject Mode:</span> ${inj.inject_mode || 'N/A'}<br/>
              <span class="label">From:</span> ${inj.from_entity || 'N/A'}<br/>
              <span class="label">To:</span> ${inj.to_entity || 'N/A'}<br/>
              <span class="label">Message:</span> ${inj.message || 'N/A'}<br/>
              <span class="label">Expected Response:</span> ${inj.expected_response || 'N/A'}<br/>
              <span class="label">Objective/Capability/Task:</span> ${inj.objective_capability_task || 'N/A'}
            </div>
          `).join('') : '<p>No MSEL injections defined</p>'}
        </div>

        <div class="section">
          <h2>Step 9: Organizations</h2>
          ${organizations.length > 0 ? organizations.map((org, idx) => `
            <div class="item">
              <strong>Organization ${idx + 1}:</strong> ${org.name}<br/>
              <span class="label">Description:</span> ${org.description || 'N/A'}<br/>
              <span class="label">Home Base:</span> ${org.home_base || 'N/A'}<br/>
              <span class="label">Contact:</span> ${org.contact_first_name || ''} ${org.contact_last_name || ''} - ${org.contact_phone || 'N/A'}
            </div>
          `).join('') : '<p>No organizations defined</p>'}
        </div>

        <div class="section">
          <h2>Step 10: Team Coordinators</h2>
          ${coordinatorsData.length > 0 ? `
            <table>
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Radio</th>
                </tr>
              </thead>
              <tbody>
                ${coordinatorsData.map(coord => `
                  <tr>
                    <td>${coord.team_name || 'N/A'}</td>
                    <td>${coord.coordinator_name || 'N/A'}</td>
                    <td>${coord.phone || 'N/A'}</td>
                    <td>${coord.radio_channel || 'N/A'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : '<p>No team coordinators defined</p>'}
        </div>

        <div class="section">
          <h2>Step 11: Code Words</h2>
          ${codeWords.length > 0 ? codeWords.map((cw, idx) => `
            <div class="item">
              <strong>${cw.word}</strong><br/>
              <span class="label">Meaning:</span> ${cw.meaning || 'N/A'}
            </div>
          `).join('') : '<p>No code words defined</p>'}
        </div>

        <div class="section">
          <h2>Step 12: Callsigns</h2>
          ${callsigns.length > 0 ? callsigns.map((cs, idx) => `
            <div class="item">
              <strong>${cs.callsign}</strong><br/>
              <span class="label">Description:</span> ${cs.description || 'N/A'}
            </div>
          `).join('') : '<p>No callsigns defined</p>'}
        </div>

        <div class="section">
          <h2>Step 13: Assumptions</h2>
          ${assumptions.length > 0 ? assumptions.map((assum, idx) => `
            <div class="item">
              <strong>${assum.assumption}</strong><br/>
              <span class="label">Description:</span> ${assum.description || 'N/A'}
            </div>
          `).join('') : '<p>No assumptions defined</p>'}
        </div>

        <div class="section">
          <h2>Step 14: Artificialities</h2>
          ${artificialities.length > 0 ? artificialities.map((art, idx) => `
            <div class="item">
              <strong>${art.artificiality}</strong><br/>
              <span class="label">Description:</span> ${art.description || 'N/A'}
            </div>
          `).join('') : '<p>No artificialities defined</p>'}
        </div>

        <div class="section">
          <h2>Step 15: Safety Concerns</h2>
          ${safetyConcerns.length > 0 ? safetyConcerns.map((safety, idx) => `
            <div class="item">
              <strong>${safety.concern}</strong><br/>
              <span class="label">Safety Officer:</span> ${safety.safety_officer || 'N/A'}<br/>
              <span class="label">Phone:</span> ${safety.phone || 'N/A'}<br/>
              <span class="label">Description:</span> ${safety.description || 'N/A'}
            </div>
          `).join('') : '<p>No safety concerns defined</p>'}
        </div>

        <div class="section">
          <h2>Step 16: Mapping</h2>
          <p><em>Map objects and markers are available in the interactive mapping interface.</em></p>
        </div>

      </body>
      </html>
    `;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (timeString && timeString.includes(':')) {
      return timeString;
    }
    return timeString || 'Not set';
  };

  const getExerciseTypeColor = (type) => {
    const colors = {
      'Table Top': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      'Drill': 'bg-green-500/20 text-green-300 border-green-500/30',
      'Functional': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      'Full Scale Exercise': 'bg-red-500/20 text-red-300 border-red-500/30',
      'No-Notice Exercise': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      'Real World Event': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  };

  if (selectedExercise) {
    return (
      <ExerciseDetailView 
        exercise={selectedExercise} 
        onBack={() => setSelectedExercise(null)} 
      />
    );
  }

  const filteredExercises = exercises.filter((e) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      (e.exercise_name || '').toLowerCase().includes(q) ||
      (e.exercise_description || '').toLowerCase().includes(q) ||
      (e.exercise_type || '').toLowerCase().includes(q) ||
      (e.location || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-orange-500 mb-2">Exercise Management</h1>
          <p className="text-sm sm:text-base text-gray-400">Manage completed emergency training exercises</p>
        </div>
        <Button 
          onClick={() => { window.location.hash = '#hira'; }}
          className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-black font-semibold"
          data-testid="new-exercise-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New HIRA
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={`skeleton-${i}`} className="bg-gray-800 border-gray-700">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-700 rounded animate-pulse"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-700 rounded animate-pulse w-1/3"></div>
                    <div className="h-3 bg-gray-700 rounded animate-pulse w-2/3"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : exercises.length === 0 ? (
        <Card className="bg-gray-800 border-gray-700 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 px-4">
            <BookOpen className="h-12 w-12 text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2 text-center">No Exercises Created Yet</h3>
            <p className="text-gray-500 text-center mb-4 text-sm sm:text-base">
              Create exercises through HIRA using the ExCon or Event buttons.
            </p>
            <Button 
              onClick={() => window.location.href = '#hira'}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Go to HIRA
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search exercises by name, description, type or location…"
              className="pl-9 pr-9 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
              data-testid="exercises-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-400 p-1"
                title="Clear search"
                data-testid="exercises-search-clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs sm:text-sm text-gray-400">
              Showing {filteredExercises.length} of {exercises.length} exercise{exercises.length !== 1 ? 's' : ''}{searchQuery ? ` matching "${searchQuery}"` : ''}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpandedExercises(new Set(filteredExercises.map(e => e.id)))}
                className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50 text-xs"
                data-testid="exercises-expand-all"
              >
                Expand All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpandedExercises(new Set())}
                className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50 text-xs"
                data-testid="exercises-collapse-all"
              >
                Collapse All
              </Button>
            </div>
          </div>
          {filteredExercises.map((exercise) => {
            const isExpanded = expandedExercises.has(exercise.id);
            return (
            <Card 
              key={exercise.id} 
              className="bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-colors cursor-pointer"
              onClick={() => setSelectedExercise(exercise)}
              data-testid={`exercise-row-${exercise.id}`}
            >
              <CardContent className={isExpanded ? 'p-4 sm:p-6' : 'p-3'}>
                <div className={`flex ${isExpanded ? 'flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-4' : 'items-center gap-3'}`}>
                  {/* Chevron toggle (compact mode) */}
                  {!isExpanded && (
                    <button
                      type="button"
                      onClick={(e) => toggleExpanded(exercise.id, e)}
                      className="flex-shrink-0 text-gray-400 hover:text-orange-400"
                      title="Expand"
                      data-testid={`exercise-toggle-${exercise.id}`}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  )}
                  {/* Exercise Image (own image via lazy cache → linked HIRA thumb → icon) */}
                  <div className={`flex-shrink-0 ${isExpanded ? 'w-full sm:w-auto flex justify-center sm:justify-start' : ''}`}>
                    <div className={`${isExpanded ? 'w-16 h-16' : 'w-10 h-10'} rounded-lg bg-gray-600 overflow-hidden flex items-center justify-center`}>
                      {(exercise.exercise_image || exImageCache[exercise.id]) ? (
                        <img 
                          src={exercise.exercise_image || exImageCache[exercise.id]} 
                          alt={exercise.exercise_name} 
                          className="w-full h-full object-cover"
                          data-testid={`exercise-own-thumb-${exercise.id}`}
                        />
                      ) : hiraThumbCache[exercise.hira_id] ? (
                        <img 
                          src={hiraThumbCache[exercise.hira_id]} 
                          alt={exercise.exercise_name} 
                          className="w-full h-full object-cover"
                          data-testid={`exercise-hira-thumb-${exercise.id}`}
                        />
                      ) : (
                        <Target className={isExpanded ? 'h-8 w-8 text-orange-500' : 'h-5 w-5 text-orange-500'} />
                      )}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0 w-full">
                    {isExpanded ? (
                      <>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-2">
                          <button
                            type="button"
                            onClick={(e) => toggleExpanded(exercise.id, e)}
                            className="flex items-center gap-2 hover:text-orange-400"
                            data-testid={`exercise-toggle-${exercise.id}`}
                          >
                            <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            <h3 className="text-lg sm:text-xl font-semibold text-white truncate">{exercise.exercise_name}</h3>
                          </button>
                          <div className="flex flex-wrap gap-2">
                            <Badge className={`${getExerciseTypeColor(exercise.exercise_type)} text-xs`}>
                              {exercise.exercise_type}
                            </Badge>
                            {exercise.organization_id && agenciesMap[exercise.organization_id] && (
                              <Badge className="bg-purple-600/20 text-purple-400 border border-purple-500/30 text-xs">
                                <Building2 className="h-3 w-3 mr-1" />
                                {agenciesMap[exercise.organization_id]}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-sm sm:text-base text-gray-400 mb-3 line-clamp-2">{exercise.exercise_description}</p>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
                          <div className="flex items-center space-x-2 text-gray-300">
                            <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-orange-500 flex-shrink-0" />
                            <span className="truncate">{exercise.location || 'Location not set'}</span>
                          </div>
                          
                          <div className="flex items-center space-x-2 text-gray-300">
                            <CalendarDays className="h-3 w-3 sm:h-4 sm:w-4 text-orange-500 flex-shrink-0" />
                            <span className="truncate">Start: {formatDate(exercise.start_date)}</span>
                          </div>
                          
                          <div className="flex items-center space-x-2 text-gray-300">
                            <CalendarDays className="h-3 w-3 sm:h-4 sm:w-4 text-orange-500 flex-shrink-0" />
                            <span className="truncate">End: {formatDate(exercise.end_date)}</span>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-3 gap-2 text-xs text-gray-500">
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            <span className="truncate">Created: {new Date(exercise.created_at).toLocaleDateString()}</span>
                            {exercise.updated_at && (
                              <span className="truncate">Modified: {new Date(exercise.updated_at).toLocaleDateString()}</span>
                            )}
                          </div>
                          <span className="truncate">Times: {formatTime(exercise.start_time)} - {formatTime(exercise.end_time)}</span>
                        </div>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => toggleExpanded(exercise.id, e)}
                        className="flex flex-wrap items-center gap-2 min-w-0 hover:text-orange-400 text-left"
                        data-testid={`exercise-title-${exercise.id}`}
                      >
                        <h3 className="text-base font-semibold text-white truncate">{exercise.exercise_name}</h3>
                        <Badge className={`${getExerciseTypeColor(exercise.exercise_type)} text-xs`}>
                          {exercise.exercise_type}
                        </Badge>
                        <span className="text-xs text-gray-500 truncate">
                          · {formatDate(exercise.start_date)} → {formatDate(exercise.end_date)}
                        </span>
                      </button>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className={`flex ${isExpanded ? 'sm:flex-col space-x-2 sm:space-x-0 sm:space-y-2 w-full sm:w-auto justify-center sm:justify-start' : 'flex-row space-x-2 flex-shrink-0'}`}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => handlePrintExerciseCard(exercise, e)}
                      className="flex-1 sm:flex-none border-blue-600 text-blue-400 hover:text-blue-300 hover:border-blue-500/50"
                      title="Print Complete Exercise"
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedExercise(exercise);
                      }}
                      className="flex-1 sm:flex-none border-blue-600 text-blue-400 hover:text-blue-300 hover:border-blue-500/50"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteExercise(exercise.id);
                      }}
                      className="flex-1 sm:flex-none border-red-600 text-red-400 hover:text-red-300 hover:border-red-500/50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );})}
        </div>
      )}
    </div>
  );
};

// Exercise Detail View

export default ExerciseListView;
